import { FAQJsonLd } from 'next-seo';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Heading } from '@/components/Cms/Heading/Heading';
import type {
  ContentfulFaqWrapFragment,
  ContentfulHeadingFragment,
} from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { messages } from '@/messages/faq';
import { theme } from '@/theme';

import { FaqItem } from './components/FaqItem/FaqItem';
import type { FaqWrapProps } from './FaqWrap';

import * as S from './FaqWrap.styles';

type RichTextNode = {
  content?: RichTextNode[];
  nodeType?: string;
  value?: string;
};

const toPlainText = (node: RichTextNode | null | undefined): string => {
  if (!node) {
    return '';
  }

  if (node.nodeType === 'text') {
    return node.value?.trim() ?? '';
  }

  const children = node.content
    ?.map((child) => toPlainText(child))
    .filter(Boolean);
  return children?.join(' ').replaceAll(/\s+/g, ' ').trim() ?? '';
};

export const mapFaqQuestionsForSchema = (
  faqListCollection: ContentfulFaqWrapFragment['faqListCollection'],
) => {
  return (faqListCollection?.items
    ?.map((item) => {
      const question = item?.faqQuestion?.trim();
      const answer = toPlainText(item?.faqAnswer?.json as RichTextNode);

      if (!question || !answer) {
        return null;
      }

      return {
        question,
        answer,
      };
    })
    .filter(Boolean) ?? []) as { answer: string; question: string }[];
};

export type FAQSectionProps = {
  collapse: ContentfulFaqWrapFragment['collapse'];
  faqListCollection: ContentfulFaqWrapFragment['faqListCollection'];
  isFromFaqWrapCategories: boolean;
  placeholderHeading: ContentfulHeadingFragment;
  withJsonLd?: boolean;
};
export const FAQSection = (props: FAQSectionProps) => {
  const [headingProps, setHeadingProps] = useState<ContentfulHeadingFragment>();
  const { formatMessage } = useIntl();
  const isMdDown = useIsMdDown();
  const {
    collapse,
    faqListCollection,
    isFromFaqWrapCategories,
    placeholderHeading,
    withJsonLd = true,
  } = props;
  useEffect(() => {
    if (placeholderHeading) {
      setHeadingProps({
        ...placeholderHeading,
        textAlignment: isMdDown ? 'center' : placeholderHeading.textAlignment,
      });
    }
  }, [isMdDown]);
  const [showAllItems, setShowAllItems] = useState(!collapse);
  const displayedItems = showAllItems
    ? faqListCollection?.items
    : faqListCollection?.items?.slice(0, 3);
  const faqJsonLdQuestions = mapFaqQuestionsForSchema(faqListCollection);
  const shouldShowMore =
    showAllItems === false &&
    faqListCollection?.items &&
    faqListCollection.items.length > 3;
  const handleShowMore = () => {
    setShowAllItems(true);
  };
  return (
    <S.Wrapper data-test-id='FAQSectionWrapper'>
      <S.Container $isFromFaqWrapCategories={isFromFaqWrapCategories}>
        {withJsonLd && faqJsonLdQuestions.length > 0 && (
          <FAQJsonLd questions={faqJsonLdQuestions} />
        )}
        {placeholderHeading && (
          <div
            id={
              placeholderHeading?.text
                ?.toLowerCase()
                .replace(/ /g, '-') as string
            }
          >
            <Heading
              Heading={headingProps}
              color={placeholderHeading?.mainColor ?? theme.colors.default}
              data-test-id='FAQSectionHeading'
            />
          </div>
        )}
        {displayedItems?.map((elem, index) => (
          <FaqItem
            key={elem?.sys?.id}
            data-test-id={`FAQSectionFaqItem-${index}`}
            faqAnswer={elem?.faqAnswer}
            faqQuestion={elem?.faqQuestion as string}
            isFromFaqWrapCategories={isFromFaqWrapCategories}
          />
        ))}
        {shouldShowMore && (
          <S.MoreResultsBox
            data-test-id='FAQSectionMoreResultsBox'
            onClick={handleShowMore}
          >
            <Box>
              <span>{formatMessage(messages.moreResults)}</span>
              <S.StyledIcon color='primary' name='chevronDown' />
            </Box>
          </S.MoreResultsBox>
        )}
      </S.Container>
    </S.Wrapper>
  );
};

export const renderContentfulFaqSection = (
  props: FaqWrapProps & { withJsonLd?: boolean },
) => {
  const isFromFaqWrapCategories = props?.isFromFaqWrapCategories ?? false;
  const collapse = props?.collapse ?? true;
  const faqListCollection = props?.faqListCollection;
  const placeholderHeading =
    props?.placeholderHeading as ContentfulHeadingFragment;
  return (
    <FAQSection
      collapse={collapse}
      faqListCollection={faqListCollection}
      isFromFaqWrapCategories={isFromFaqWrapCategories}
      placeholderHeading={placeholderHeading}
      withJsonLd={props?.withJsonLd}
    />
  );
};
